export class ApiService {
  constructor() {
    this.endpoint = "";
    this.per = 10;
  }

  getHeaders() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      'X-Requested-With': 'XMLHttpRequest'
    };
  }

  // Session expired / not authenticated — send the user to log in (DONATE-1626).
  handleUnauthorized() {
    window.location.href = `/users/sign_in`;
  }

  post(url, params) {
    return fetch(url, {
      headers: this.getHeaders(),
      body: JSON.stringify(params),
      method: "POST",
    }).then((response) => response.json().then((json) => ({
      status: response.status,
      json,
    })));
  }

  patch(url, params) {
    return fetch(url, {
      headers: this.getHeaders(),
      body: JSON.stringify(params),
      method: "PATCH"
    }).then((response) => response.json().then((json) => ({
      status: response.status,
      json,
    })));
  }

  upload(url, params, method = "PATCH") {
    return fetch(url, {
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body: params,
      method: method
    }).then((response) => response.json().then((json) => ({
      status: response.status,
      json,
    })));
  }

  destroy(url, params) {
    let init = {
      headers: this.getHeaders(),
      method: "DELETE",
    };

    const finalUrl = (params) ? `${url}?${params.toString()}` : `${url}`;
    return fetch(finalUrl, init).then((response) => response.json().then((json) => ({
      status: response.status,
      json,
    }))).catch((e) => {
      throw new Error(e);
    });

  }

  get(url, params) {
    let init = {
      headers: this.getHeaders(),
      method: "GET",
    };

    const finalUrl = (params) ? `${url}?${params.toString()}` : `${url}`;
    return fetch(finalUrl, init).then((response) => {
      // Session expired / unauthenticated — redirect to login instead of crashing the SPA.
      if (response.status === 401) {
        this.handleUnauthorized();
        return { status: 401, json: {} };
      }
      return response.json().then((json) => {
        if (response.status !== 200) {
          throw new Error("Record Not Found");
        }
        return {
          status: response.status,
          json
        };
      });
    }).catch((e) => {
      throw new Error(e);
    });
  }

  async streamToBlob(stream, mimeType = 'text/csv') {
    const chunks = [];
    const reader = stream.getReader();

    // Read the stream and collect chunks
    for (let result; !(result = await reader.read()).done;) {
      chunks.push(result.value);
    }

    // Return the combined Blob
    return new Blob(chunks, {type: mimeType});
  }

  getFile(resource, params = {}) {
    const url = new URL(resource, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        if (Array.isArray(params[key])) {
          // Append each array element as a separate parameter
          params[key].forEach(value => {
            url.searchParams.append(`${key}[]`, value);
          });
        } else {
          url.searchParams.append(key, params[key]);
        }      }
    });

    let init = {
      headers: this.getHeaders(),
      method: "GET",
      responseType: 'blob', // important
    };
    return fetch(url.toString(), init).then((response) => ({
        status: response.status,
        data: response.body,
      })
    ).catch((error) => {
      throw new Error(`ApiService (${resource}) ${error}`);
    });
  }

  downloadFile(url, fileName, mimeType = 'text/csv', params = {}) {
    return this.getFile(url, params)
      .then(async (response) => {
        // Don't save error responses (e.g. a 403 for giving-blindness) as a file —
        // the body is JSON and would download as a corrupt .xlsx/.pdf. Surface the
        // error message instead so the caller can show it.
        if (response.status < 200 || response.status >= 300) {
          const errorBlob = await this.streamToBlob(response.data, 'application/json');
          let message = 'Download failed';
          try {
            const parsed = JSON.parse(await errorBlob.text());
            if (parsed.error) {
              message = parsed.error;
            } else if (parsed.errors) {
              message = Array.isArray(parsed.errors)
                ? parsed.errors.join(', ')
                : String(parsed.errors);
            }
          } catch {
            // non-JSON error body; keep the default message
          }
          throw new Error(message);
        }

        const newBlob = await this.streamToBlob(response.data, mimeType);
        newBlob.name = fileName;
        newBlob.lastModifiedDate = new Date();

        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const fileURL = window.URL.createObjectURL(newBlob);
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', newBlob.name);
        document.body.appendChild(fileLink);
        fileLink.click();
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(fileURL);
        }, 100);
      });
  }

  responseHasErrors(response) {
    return Object.prototype.hasOwnProperty.call(response.json, 'errors')
      || Object.prototype.hasOwnProperty.call(response.json, 'error')
      || response.status === 404
      || response.status === 500;
  }

  show(id) {
    if (!this.endpoint) {
      throw new Error("Cannot call from superclass");
    }

    return this.get(`${this.endpoint}/${id}`).then((response) => {
      return response.json;
    }).catch((e) => {
      throw new Error(e);
    });
  }
  /**
   * COMMON INDEX MODEL METHOD
   * Calls the GET endpoint for the model
   * @param params -> Passed URL search params
   * @param filter -> Filter object shared in model stores, will build ransack query params
   * Format: {
   *       sort: {
   *         key: "created_at", -> Ransack key
   *         order: "asc" -> ascending or descending
   *       },
   *       search: {
   *         key: "user_first_name_cont", // Ransack search key, set in model stores
   *         term: "Joe" -> Query search term
   *       },
   *       filters: { -> Dropdown select filters. Key matches model field, value must be exact match
   *         role: 'Owner' -> Would add role_eq, Owner to query params, Many values accepted for deeper filtering
   *       }
   *     },
   * @returns {Promise<Object>}
   */
  index(params, filter = null) {
    if (!this.endpoint) {
      throw new Error("Cannot call from superclass");
    }
    if(filter && !params) {
      params = new URLSearchParams();
    }
    if (filter?.sort?.key) {
      params.set('query[s]', `${filter.sort.key} ${filter.sort.order}`);
    }
    if (filter?.search?.key && filter?.search?.term) {
      params.set(`query[${filter.search.key}]`, filter.search.term);
    }

    if(filter?.filters) {
      Object.keys(filter.filters).forEach((key) => {
        if(filter.filters[key]) {
          params.set(`query[${key}_eq]`, filter.filters[key]);
        }
      });
    }

    return this.get(`${this.endpoint}`, params).then((response) => {
      // On an expired session (401) return a safe, empty result so consumers
      // never receive a bare {} that crashes the index render (DONATE-1626).
      if (response?.status === 401) {
        return { data: [], meta: { page: 1, pages: 1, limit: this.per, count: 0 } };
      }
      return response?.json;
    }).catch((e) => {
      console.warn('error on base index method', e);
      return { data: [], meta: { page: 1, pages: 1, limit: this.per, count: 0 } };
    });
  }

  // Helper method for pagination. Defaults to show 10 records.
  paginate(page, params = null, filter = null) {
    if (!params) {
      params = new URLSearchParams();
    }
    params.set('limit', this.per);
    params.set('page', page);

    return this.index(params, filter);
  }

  // Index all without pagination
  indexAll(params = null, filter = null) {
    if (!params) {
      params = new URLSearchParams();
    }
    params.set('limit', 1000000);

    return this.index(params, filter);
  }

  async delete(params) {
    if (!this.endpoint) {
      throw new Error("Cannot call from superclass");
    }

    try {
      let response = await this.destroy(`${this.endpoint}`, params);
      return response.json;
    } catch {
      return {};
    }
  }

  async update(id, params) {
    if (!this.endpoint) {
      throw new Error("Cannot call from superclass");
    }

    try {
      let url = `${this.endpoint}/${id}`;
      return await this.patch(url, params);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async create(params) {
    if (!this.endpoint) {
      throw new Error("Cannot call from superclass");
    }

    try {
      let url = `${this.endpoint}`;
      return await this.post(url, params);
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}
